import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import adminAgentsService from '@/services/adminAgents.service';
import { useToast } from '@/contexts/ToastContext';
import { useAuthContext } from '@/contexts/AuthContext';
import { AdminAgent, AdminAgentForm } from '@/types/adminAgent';
import LoadingSpinner from '@/components/LoadingSpinner';
import DataTab from '@/components/admin/agent-form/DataTab';
import FilesTab from '@/components/admin/agent-form/FilesTab';
import DistributionTab from '@/components/admin/agent-form/DistributionTab';
import SqlConfigTab from '@/components/admin/agent-form/SqlConfigTab';

type TabId = 'data' | 'files' | 'distribution' | 'sql';

const EMPTY_FORM: AdminAgentForm = {
  name: '',
  description: '',
  avatar: '🤖',
  type: 'RAG',
  scope: 'PERSONAL',
  is_active: true,
  is_published: false,
  model: 'gpt-5.4-mini',
  temperature: 0.3,
  system_prompt: 'Você é um assistente útil.',
  sql_config: {},
  sector_ids: [],
};

function toForm(agent: AdminAgent): AdminAgentForm {
  return {
    name: agent.name,
    description: agent.description,
    avatar: agent.avatar,
    type: agent.type,
    scope: agent.scope,
    is_active: agent.is_active,
    is_published: agent.is_published,
    model: agent.model,
    temperature: agent.temperature,
    system_prompt: agent.system_prompt,
    sql_config: agent.sql_config || {},
    sector_ids: agent.sectors.map((s) => s.id),
  };
}

const AdminAgentFormPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuthContext();
  const isEdit = !!id;
  const isAdmin = user?.role === 'ADMIN';

  const [tab, setTab] = useState<TabId>('data');
  const [form, setForm] = useState<AdminAgentForm>(EMPTY_FORM);
  const [agent, setAgent] = useState<AdminAgent | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [sqlJsonError, setSqlJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    adminAgentsService
      .get(id)
      .then((a) => {
        setAgent(a);
        setForm(toForm(a));
      })
      .catch(() => showToast('Não foi possível carregar o agente.', 'error'))
      .finally(() => setLoading(false));
  }, [id, isEdit, showToast]);

  const patch = (p: Partial<AdminAgentForm>) => setForm((prev) => ({ ...prev, ...p }));

  const tabs: { id: TabId; label: string }[] = [
    { id: 'data', label: 'Dados' },
    { id: 'files', label: 'Arquivos' },
    { id: 'distribution', label: 'Distribuição' },
    ...(form.type === 'SQL' ? [{ id: 'sql' as TabId, label: 'Config SQL' }] : []),
  ];

  // Se o user trocou pra RAG enquanto estava na tab SQL, volta pra Data
  useEffect(() => {
    if (tab === 'sql' && form.type !== 'SQL') setTab('data');
  }, [tab, form.type]);

  const buildPayload = (): Partial<AdminAgentForm> | null => {
    // Valida JSON do row_filters quando type=SQL
    const cfg = { ...(form.sql_config || {}) } as Record<string, unknown>;
    if (form.type === 'SQL' && typeof cfg.row_filters === 'string') {
      const raw = (cfg.row_filters as string).trim();
      if (raw) {
        try {
          cfg.row_filters = JSON.parse(raw);
        } catch (e) {
          setSqlJsonError('JSON inválido em row_filters.');
          return null;
        }
      } else {
        cfg.row_filters = {};
      }
    }
    setSqlJsonError(null);
    return {
      ...form,
      sql_config: form.type === 'SQL' ? cfg : {},
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) {
      setTab('sql');
      showToast('Corrija o JSON de row_filters antes de salvar.', 'error');
      return;
    }
    if (!form.name.trim()) {
      setTab('data');
      showToast('Dê um nome ao agente.', 'error');
      return;
    }
    setSaving(true);
    try {
      if (isEdit && id) {
        await adminAgentsService.update(id, payload);
        showToast('Agente atualizado.', 'success');
      } else {
        const created = await adminAgentsService.create(payload);
        showToast('Agente criado.', 'success');
        navigate(`/admin/agentes/${created.id}/editar`, { replace: true });
        return;
      }
      navigate('/admin/agentes');
    } catch (err) {
      const code = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      if (code && /curator_cant/.test(code)) {
        showToast('Apenas admins podem criar/promover agentes GLOBAL.', 'error');
      } else {
        showToast('Erro ao salvar agente.', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async (op: 'publish' | 'unpublish' | 'activate' | 'deactivate') => {
    if (!id) return;
    try {
      const updated = await adminAgentsService[op](id);
      setAgent(updated);
      setForm(toForm(updated));
      showToast('Atualizado.', 'success');
    } catch {
      showToast('Erro ao executar ação.', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto" data-testid="admin-agent-form">
      <header className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-content-primary">
          {isEdit ? `Editar ${agent?.name ?? 'agente'}` : 'Novo agente'}
        </h1>
        {isEdit && agent && (
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="action-usage"
              onClick={() => navigate(`/admin/agentes/${agent.id}/uso`)}
              className="text-sm px-3 py-1.5 rounded-lg border border-surface-border hover:border-content-secondary inline-flex items-center gap-1"
            >
              <span className="material-symbols-outlined text-base">analytics</span>
              Ver uso
            </button>
            {agent.is_published ? (
              <button
                type="button"
                data-testid="action-unpublish"
                onClick={() => handleAction('unpublish')}
                className="text-sm px-3 py-1.5 rounded-lg border border-surface-border hover:border-content-secondary"
              >
                Despublicar
              </button>
            ) : (
              <button
                type="button"
                data-testid="action-publish"
                onClick={() => handleAction('publish')}
                className="text-sm px-3 py-1.5 rounded-lg bg-green-600 text-white hover:opacity-90"
              >
                Publicar
              </button>
            )}
            {agent.is_active ? (
              <button
                type="button"
                data-testid="action-deactivate"
                onClick={() => handleAction('deactivate')}
                className="text-sm px-3 py-1.5 rounded-lg border border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
              >
                Desativar
              </button>
            ) : (
              <button
                type="button"
                data-testid="action-activate"
                onClick={() => handleAction('activate')}
                className="text-sm px-3 py-1.5 rounded-lg bg-green-600 text-white hover:opacity-90"
              >
                Ativar
              </button>
            )}
          </div>
        )}
      </header>

      <div className="border-b border-surface-border flex gap-1 mb-4">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`tab-${t.id}-btn`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 border-b-2 text-sm font-medium transition ${
              tab === t.id
                ? 'border-accent text-accent'
                : 'border-transparent text-content-muted hover:text-content-primary'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'data' && (
        <DataTab form={form} onChange={patch} allowSql={isAdmin} />
      )}
      {tab === 'files' && <FilesTab agentId={isEdit ? id ?? null : null} />}
      {tab === 'distribution' && (
        <DistributionTab form={form} onChange={patch} allowGlobal={isAdmin} />
      )}
      {tab === 'sql' && form.type === 'SQL' && (
        <SqlConfigTab form={form} onChange={patch} jsonError={sqlJsonError} />
      )}

      <div className="mt-6 flex gap-2 justify-end">
        <button
          type="button"
          onClick={() => navigate('/admin/agentes')}
          className="px-4 py-2 text-sm rounded-lg text-content-secondary hover:text-content-primary"
        >
          Cancelar
        </button>
        <button
          type="button"
          data-testid="save"
          disabled={saving}
          onClick={handleSave}
          className="bg-accent text-surface-primary font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50"
        >
          {saving ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </div>
  );
};

export default AdminAgentFormPage;
